import type { NavigationAction } from '@react-navigation/native'
import { usePreventRemove } from '@react-navigation/native'
import { useNavigation } from 'expo-router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Platform } from 'react-native'
import { Dialog, Button as PaperButton, Portal, Text, useTheme } from 'react-native-paper'

export function UnsavedChangesDialog({ isDirty }: { isDirty: boolean }) {
    const theme = useTheme()
    const navigation = useNavigation()
    const [visible, setVisible] = useState(false)
    const [confirmed, setConfirmed] = useState(false)
    const pendingAction = useRef<NavigationAction | null>(null)

    useEffect(() => {
        if (Platform.OS === 'web') {
            if (!isDirty) return
            const handler = (e: BeforeUnloadEvent) => {
                e.preventDefault()
            }
            window.addEventListener('beforeunload', handler)
            return () => window.removeEventListener('beforeunload', handler)
        }
    }, [isDirty])

    usePreventRemove(isDirty && !confirmed, ({ data }) => {
        pendingAction.current = data.action
        setVisible(true)
    })

    const onStay = useCallback(() => {
        pendingAction.current = null
        setVisible(false)
    }, [])

    const onDiscard = useCallback(() => {
        setVisible(false)
        setConfirmed(true)
    }, [])

    useEffect(() => {
        if (!confirmed) return
        if (!pendingAction.current) return
        navigation.dispatch(pendingAction.current)
        pendingAction.current = null
        setConfirmed(false)
    }, [confirmed, navigation])

    return (
        <Portal>
            <Dialog
                visible={visible}
                onDismiss={onStay}
                style={{ maxWidth: 400, alignSelf: 'center', width: '100%' }}
            >
                <Dialog.Title>Unsaved Changes</Dialog.Title>
                <Dialog.Content>
                    <Text variant="bodyMedium">
                        You have unsaved changes. Are you sure you want to leave?
                    </Text>
                </Dialog.Content>
                <Dialog.Actions>
                    <PaperButton onPress={onStay}>Stay</PaperButton>
                    <PaperButton onPress={onDiscard} textColor={theme.colors.error}>
                        Discard
                    </PaperButton>
                </Dialog.Actions>
            </Dialog>
        </Portal>
    )
}
